import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { cls } from "app/utils";
import upload, { SubscriberPromise } from "app/utils/uploader";
import pdfIcon from "../../../../../../public/images/ico_pdf.svg";
import styles from "./styles.module.scss";

interface UploadedFile {
  id: string;
  name: string;
  progress?: number;
  error?: boolean;
  isFromApi?: boolean;
}

interface IProps {
  termsDocuments?: { object?: string; filename?: string; url?: string }[];
  onSave?: (_termsDocumentIds: string[]) => void;
}

const TermsConditionsConfig = ({ termsDocuments, onSave }: IProps) => {
  const { t } = useTranslation();
  const uploadRefs = useRef<Record<string, SubscriberPromise>>({});
  const isMounted = useRef(true);

  const initialFiles: UploadedFile[] = (termsDocuments || [])
    .filter((doc) => doc.filename)
    .map((doc) => ({ id: doc.url || doc.filename!, name: doc.filename!, isFromApi: true }));

  const [files, setFiles] = useState<UploadedFile[]>(initialFiles);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      Object.values(uploadRefs.current).forEach((ref) => {
        if (ref && !ref.isAborted) {
          ref.abort();
        }
      });
      uploadRefs.current = {};
    };
  }, []);

  // Sync files when termsDocuments prop changes (e.g. after API refresh)
  useEffect(() => {
    const existingDocs: UploadedFile[] = (termsDocuments || [])
      .filter((doc) => doc.filename)
      .map((doc) => ({ id: doc.url || doc.filename!, name: doc.filename!, isFromApi: true }));

    setFiles((prev) => {
      // Keep any files that are still uploading (have progress)
      const uploading = prev.filter((f) => f.progress != null);
      // Merge: existing docs from API + any in-progress uploads
      const existingIds = new Set(existingDocs.map((d) => d.id));
      const newUploading = uploading.filter((f) => !existingIds.has(f.id));
      return [...existingDocs, ...newUploading];
    });
  }, [termsDocuments]);

  const uploadFile = useCallback(
    (file: File) => {
      const tempId = `uploading-${Date.now()}-${file.name}`;
      const newFile: UploadedFile = {
        id: tempId,
        name: file.name,
        progress: 0,
      };

      setFiles((prev) => [...prev, newFile]);

      const abortableUpload = upload(
        file,
        file.name,
        file.type,
        "workflow_terms_document",
      );
      uploadRefs.current[tempId] = abortableUpload;

      abortableUpload.onProgress = (progressEvent) => {
        if (!isMounted.current) {
          return;
        }
        let pct = 0;
        if (
          progressEvent &&
          "loaded" in progressEvent &&
          "total" in progressEvent &&
          progressEvent.total > 0
        ) {
          pct = Math.floor((progressEvent.loaded / progressEvent.total) * 100);
        }
        setFiles((prev) =>
          prev.map((f) => (f.id === tempId ? { ...f, progress: pct } : f)),
        );
      };

      abortableUpload.promise
        .then((result: { id: string; filename: string }) => {
          if (!isMounted.current) {
            return;
          }

          const realId = result.id;
          const realName = result.filename || file.name;

          // Replace temp entry with real uploaded file
          setFiles((prev) => {
            const updated = prev.map((f) =>
              f.id === tempId
                ? { id: realId, name: realName, progress: undefined }
                : f,
            );
            // Notify parent with only newly uploaded file IDs (not API-loaded docs)
            const completedIds = updated
              .filter((f) => f.progress == null && !f.error && !f.isFromApi)
              .map((f) => f.id);
            onSave?.(completedIds);
            return updated;
          });

          delete uploadRefs.current[tempId];
        })
        .catch((err: any) => {
          if (!isMounted.current) {
            return;
          }
          if (
            err?.message === "UPLOAD_CANCELLED" ||
            abortableUpload.isAborted
          ) {
            // Remove cancelled upload from list
            setFiles((prev) => prev.filter((f) => f.id !== tempId));
          } else {
            // Mark as errored
            setFiles((prev) =>
              prev.map((f) =>
                f.id === tempId
                  ? { ...f, progress: undefined, error: true }
                  : f,
              ),
            );
          }
          delete uploadRefs.current[tempId];
        });
    },
    [onSave],
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      acceptedFiles.forEach((file) => uploadFile(file));
    },
    [uploadFile],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "application/pdf": [".pdf"] },
    multiple: true,
  });

  const handleRemove = (fileId: string) => {
    // Abort if still uploading
    if (uploadRefs.current[fileId]) {
      uploadRefs.current[fileId].abort();
      delete uploadRefs.current[fileId];
    }

    setFiles((prev) => {
      const updated = prev.filter((f) => f.id !== fileId);
      // Only include newly uploaded file IDs (not API-loaded docs)
      const completedIds = updated
        .filter((f) => f.progress == null && !f.error && !f.isFromApi)
        .map((f) => f.id);
      onSave?.(completedIds);
      return updated;
    });
  };

  const handleRetry = (file: UploadedFile) => {
    setFiles((prev) => prev.filter((f) => f.id !== file.id));
    // Re-trigger is not possible without the original File object,
    // so we just remove the errored entry and let the user re-drop
  };

  return (
    <div className={styles.container}>
      <p className={styles.description}>
        {t(
          "You can upload several PDF files containing the Terms and Conditions of your facility.",
        )}
      </p>

      <div
        {...getRootProps()}
        className={cls(styles.dropzone, {
          [styles.dropzone_active]: isDragActive,
        })}
      >
        <input {...getInputProps()} />
        <i className={cls("ico", "ico-document", styles.dropzone_icon)} />
        <span className={styles.dropzone_text}>
          {t("Drop your files here or")}{" "}
          <span className={styles.browse_link}>{t("browse")}</span>
        </span>
      </div>

      {files.length > 0 && (
        <div className={styles.file_list}>
          {files.map((file) => (
            <div
              key={file.id}
              className={cls(styles.file_item, {
                [styles.file_item_error]: file.error,
              })}
            >
              {file.progress != null ? (
                <>
                  <div className={styles.spinner} />
                  <span className={styles.progress_text}>
                    {t("Uploading...")}{" "}
                    <strong>{file.progress}%</strong>
                  </span>
                </>
              ) : file.error ? (
                <>
                  <span className={styles.error_text}>
                    {t("Upload failed")}
                  </span>
                  <button
                    className={styles.retry_btn}
                    onClick={() => handleRetry(file)}
                  >
                    {t("Remove")}
                  </button>
                </>
              ) : (
                <>
                  <img src={pdfIcon} alt="PDF" className={styles.pdf_icon} />
                  <span className={styles.file_name}>{file.name}</span>
                </>
              )}
              <button
                className={styles.remove_btn}
                onClick={() => handleRemove(file.id)}
              >
                &times;
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TermsConditionsConfig;
